import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useMarket } from '../../../hooks/useSDI'
import { cn } from '@/shared/lib/utils'
import { ArrowLeft, ChevronRight, Shield } from 'lucide-react'

const tabs = [
  { id: 'overview', label: 'Vue d\'ensemble', path: '' },
  { id: 'keywords', label: 'Mots-clés', path: '/keywords' },
  { id: 'budget', label: 'Budget', path: '/budget' },
  { id: 'opportunities', label: 'Opportunités', path: '/opportunities' },
  { id: 'setup', label: 'Configuration', path: '/setup' },
]

function ConfidenceBadge({ score, level }: { score: number | null; level: string | null }) {
  if (score === null) return null
  const cfg = {
    high: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Élevée' },
    medium: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Moyenne' },
    low: { bg: 'bg-red-100', text: 'text-red-700', label: 'Faible' },
  }[level ?? 'low'] ?? { bg: 'bg-red-100', text: 'text-red-700', label: 'Faible' }

  return (
    <span className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold', cfg.bg, cfg.text)}>
      <Shield size={14} /> {score.toFixed(0)}/100 — {cfg.label}
    </span>
  )
}

function getActiveTab(pathname: string, marketId: string): string {
  const base = `/sdi/markets/${marketId}`
  const suffix = pathname.replace(base, '')
  if (suffix === '' || suffix === '/') return 'overview'
  if (suffix.startsWith('/keywords')) return 'keywords'
  if (suffix.startsWith('/budget')) return 'budget'
  if (suffix.startsWith('/opportunities')) return 'opportunities'
  if (suffix.startsWith('/setup')) return 'setup'
  return 'overview'
}

export function MarketTabsLayout({ children }: { children: React.ReactNode }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: market, isLoading } = useMarket(id!)

  const activeTab = getActiveTab(location.pathname, id!)
  const activeLabel = tabs.find((t) => t.id === activeTab)?.label ?? 'Vue d\'ensemble'

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-4 w-48 bg-surface-100 rounded" />
        <div className="h-8 w-64 bg-surface-100 rounded" />
        <div className="h-10 w-full bg-surface-100 rounded" />
        <div className="h-64 bg-surface-100 rounded-xl" />
      </div>
    )
  }

  if (!market) {
    return (
      <div className="text-center py-16">
        <p className="text-surface-500">Marché introuvable</p>
        <button onClick={() => navigate('/sdi/markets')} className="text-teal text-sm mt-2">Retour à la liste</button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div>
        <div className="flex items-center gap-1.5 text-xs text-surface-400 mb-3">
          <button onClick={() => navigate('/sdi/markets')} className="hover:text-surface-600">Marchés</button>
          <ChevronRight size={12} />
          <span className="text-surface-600">{market.name}</span>
          <ChevronRight size={12} />
          <span className="text-surface-600">{activeLabel}</span>
        </div>

        {/* Header */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/sdi/markets')} className="p-2 rounded-lg hover:bg-surface-100 transition-colors">
            <ArrowLeft size={20} className="text-surface-500" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-surface-900">{market.name}</h1>
              <span className="text-xs px-2 py-0.5 rounded-full bg-surface-100 text-surface-500">{market.country} · {market.language}</span>
              <ConfidenceBadge score={market.confidence_score} level={market.confidence_level} />
            </div>
            <p className="text-surface-500 mt-1">{market.keyword_count} mot(s)-clé(s) · {market.currency}</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 border-b border-surface-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => navigate(`/sdi/markets/${id}${tab.path}`)}
            className={cn(
              'px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
              tab.id === activeTab
                ? 'border-teal text-teal'
                : 'border-transparent text-surface-500 hover:text-surface-700'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Page content */}
      {children}
    </div>
  )
}
